/**
 * GTM Analytics Service
 * Service for pushing events to Google Tag Manager dataLayer
 *
 * @author Krzysztof Guz
 */
import { Injectable } from '@angular/core';

declare global {
    interface Window {
        dataLayer: any[];
    }
}

export interface GtmEventData {
    [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class GtmAnalyticsService {
    /**
     * Traffic flow for this tab session ('salesforce_new' | 'salesforce_update' | 'web'),
     * attached to every pushed event. Detected by the inline script in index.html before
     * GTM loads — the sessionStorage key and detection logic must stay in sync with it.
     */
    private readonly trafficFlow = this.resolveTrafficFlow();

    /**
     * Resolved A/B experiment variants (experiment key → variant), registered by
     * AbTestService. Attached to every pushed event so the whole funnel can be
     * segmented by variant without relying on the one-off exposure event.
     */
    private readonly experimentContext = new Map<string, string>();

    /**
     * Register a resolved experiment variant. From this point on, every event
     * pushed to the dataLayer carries it as an `exp_<experimentKey>` parameter.
     */
    setExperimentContext(experimentKey: string, variant: string): void {
        this.experimentContext.set(experimentKey, variant);
    }

    /**
     * Push an event to the GTM dataLayer
     * @param eventName - The name of the event to push
     * @param eventData - Optional additional data to include with the event
     */
    pushEvent(eventName: string, eventData?: GtmEventData): void {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: eventName,
            traffic_flow: this.trafficFlow,
            ...this.experimentContextParams(),
            ...eventData
        });
    }

    private experimentContextParams(): GtmEventData {
        const params: GtmEventData = {};
        this.experimentContext.forEach((variant, experimentKey) => {
            params[`exp_${experimentKey}`] = variant;
        });
        return params;
    }

    private resolveTrafficFlow(): string {
        try {
            const stored = sessionStorage.getItem('oms_traffic_flow');
            if (stored) {
                return stored;
            }
        } catch (e) {
            // sessionStorage unavailable — fall through to URL detection
        }
        const path = window.location.pathname;
        if (path.indexOf('/sf/update/') === 0) {
            return 'salesforce_update';
        }
        return path.indexOf('/sf/') === 0 ? 'salesforce_new' : 'web';
    }

    // Step 1: Campus & Club Selection Events
    trackCampusSelected(campusName: string): void {
        this.pushEvent('campus_selected', { campus_name: campusName });
    }

    trackClubSelected(clubCode: string, clubName?: string): void {
        this.pushEvent('club_selected', { club_code: clubCode, club_name: clubName });
    }

    // Step 3: Membership Selection Events
    trackMembershipSelected(productId: string, productName: string): void {
        this.pushEvent('membership_selected', { product_id: productId, product_name: productName });
    }

    trackMembershipBuilderContinue(productId: string, productName: string, clubCode: string): void {
        this.pushEvent('membership_builder_continue', {
            product_id: productId,
            product_name: productName,
            club_code: clubCode
        });
    }

    // Details Page Events
    trackPersonalDetailsContinue(): void {
        this.pushEvent('personal_details_continue');
    }

    // Agreements Page Events
    trackAgreementAccepted(agreementType: string): void {
        this.pushEvent('agreement_accepted', { agreement_type: agreementType });
    }

    trackAgreementsAcceptAll(): void {
        this.pushEvent('agreements_accept_all');
    }

    // Checkout Page Events
    trackCheckoutAgreementAccepted(): void {
        this.pushEvent('checkout_agreement_accepted');
    }

    trackPurchaseClicked(purchaseData: {
        clubCode: string;
        productName: string;
        purchaseValue: number;
    }): void {
        this.pushEvent('purchase_clicked', {
            club_code: purchaseData.clubCode,
            product_name: purchaseData.productName,
            purchase_value: purchaseData.purchaseValue
        });
    }

    // Step Completion Events
    trackFinishedSelectedMembershipView(): void {
        this.pushEvent('finished_selected_membership_view_event');
    }

    trackFinishedUserDetailsView(): void {
        this.pushEvent('finished_user_details_view_event');
    }

    trackFinishedAgreementsView(experimentName?: string, variantName?: string): void {
        const data = experimentName && variantName
            ? { experiment_name: experimentName, variant_name: variantName }
            : undefined;
        this.pushEvent('finished_aggrements_view_event', data);
    }

    trackFinishedCheckoutView(): void {
        this.pushEvent('finished_checkout_view_event');
    }

    // A/B Testing Events
    trackAbTestExposure(experimentName: string, variantName: string): void {
        this.pushEvent('ab_test_exposure', { experiment_name: experimentName, variant_name: variantName });
    }
}
